import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { ManageReleasePayloadType } from '@/types';

import useBreakpoint from '@/hooks/use-break-point';
import { API_ROUTES } from '@/query/routes';
import axiosInstance from '@/utils/api/axios-instance';
import showReleaseToast from '@/utils/helpers/show-release-toast';

const useUpdateReleaseNote = (id: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const isMobile = useBreakpoint('mobile');

  return useMutation<unknown, Error, ManageReleasePayloadType>({
    mutationKey: ['update-release-note', id],
    mutationFn: async (updatedReleaseNoteData) => {
      const { data } = await axiosInstance.patch(
        API_ROUTES.releaseNote(id),
        updatedReleaseNoteData
      );

      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['release-note-lists'] });
      queryClient.invalidateQueries({ queryKey: ['release-note-lists', id] });
      showReleaseToast({
        variant: 'success',
        title: t('release_note.update.title'),
        description: t('release_note.update.description'),
        isMobile,
      });
    },

    onError: () => {
      showReleaseToast({
        variant: 'error',
        title: t('errors.general'),
        description: t('errors.update_release_note.api'),
        isMobile,
      });
    },
  });
};

export default useUpdateReleaseNote;
